import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  User, Mail, Phone, MapPin, Edit2, Upload, 
  Package, Bell, Wallet, HelpCircle, Settings, 
  CreditCard, Star, ShoppingBag, MessageSquare,
  ChevronRight, LogOut, Shield, Globe, Lock,
  Clock, CheckCircle, AlertCircle, Truck, MessageCircle,
  ArrowLeft, Home, Heart, Settings as SettingsIcon,
  ShieldCheck, FileText, Users, Award, Calendar,
  QrCode, Search, Filter, History, Gift,
  Eye, EyeOff, Key
} from 'lucide-react';
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { 
  getUser, 
  getWalletBalance,
  getUserNotifications,
  updateUser,
  markNotificationAsRead, 
} from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';

const Profile = () => {
  const { user: authUser, logout, updateUser: updateAuthUser } = useAuth();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    balance: 0,
    notifications: 0,
    rating: 0
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [notifications, setNotifications] = useState([]);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Brand colors
  const colors = {
    primary: '#ff660e',
    black: '#0f0e0e',
    white: '#ffffff',
    lightBg: '#f8f9fa',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  };

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
      return;
    }
    
    fetchUserProfile();
    fetchStats();
    fetchAllNotifications();
  }, [authUser, navigate]);

  const fetchUserProfile = async () => {
    try {
      const response = await getUser(authUser.user_id);
      if (response.data) {
        setUser(response.data);
        setFormData(response.data);
        setStats(prev => ({
          ...prev,
          rating: parseFloat(response.data.rating_avg) || 0
        }));
      }
    } catch (error) {
      // console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Get wallet balance from your wallet dashboard API
      const walletResponse = await getWalletBalance(authUser.user_id);
      const balance = walletResponse.success ? parseFloat(walletResponse.balance) || 0 : 0;
      
      setStats(prev => ({
        ...prev,
        balance
      }));
    } catch (error) {
      // console.error('Error fetching stats:', error);
    }
  };

  const fetchAllNotifications = async () => {
    try {
      const response = await getUserNotifications();
      if (response.success && response.data) {
        setNotifications(response.data);
        setStats(prev => ({
          ...prev,
          notifications: response.unread_count || 0
        }));
      }
    } catch (error) {
      // console.error('Error fetching notifications:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      const response = await updateUser(authUser.user_id, formData);
      if (response.success) {
        setUser(response.data);
        setEditing(false);
        updateAuthUser(response.data); // Update auth context
        alert('Profile updated successfully!');
      }
    } catch (error) {
      // console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    try {
      const response = await updateUser(authUser.user_id, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      if (response.success) {
        alert('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPassword(false);
      }
    } catch (error) {
      // console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.is_read);
      for (const notification of unreadNotifications) {
        await markNotificationAsRead(notification.id);
      }
      
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setStats(prev => ({ ...prev, notifications: 0 }));
    } catch (error) {
      // console.error('Error marking notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_shipped':
      case 'order_delivered':
        return <Truck className="w-4 h-4 text-blue-500" />;
      case 'order_accepted':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'password_changed':
      case 'password_reset_verified':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'support_message':
      case 'support_created':
        return <MessageCircle className="w-4 h-4 text-indigo-500" />;
      case 'dispute_raised':
      case 'dispute_against':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMins > 0) {
      return `${diffMins}m ago`;
    } else {
      return 'Just now';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">My Profile</h1>
            
            <button
              onClick={() => setActiveTab('settings')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <SettingsIcon className="w-5 h-5 text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Profile Header */}
      <div className="px-4 py-4">
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center gap-3 mb-4">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-2 border-white shadow overflow-hidden bg-gradient-to-br from-[#ff660e] to-orange-500">
                {user?.profile_picture_url ? (
                  <img 
                    src={getImageUrl(user.profile_picture_url)} 
                    alt={user.first_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>
              <button 
               onClick={() => navigate('/settings')}
                className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow border"
              >
                <Edit2 className="w-3 h-3 text-[#ff660e]" />
              </button>
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h2 className="text-lg font-bold text-black mb-1">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-gray-600 text-sm mb-2 truncate">{user?.email}</p>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center text-sm">
                  <Star className="w-4 h-4 fill-current text-yellow-400 mr-1" />
                  <span className="font-semibold text-black">{stats.rating.toFixed(1)}</span>
                </div>
                <div className="flex items-center text-gray-600 text-sm">
                  <MapPin className="w-3 h-3 mr-1" />
                  <span className="truncate">{user?.state_name || 'Location'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2 border-t pt-4">
            <button 
              onClick={() => navigate('/chats')}
              className="flex flex-col items-center p-2 hover:bg-gray-50 rounded-lg"
            >
              <ShoppingBag className="w-5 h-5 text-[#ff660e] mb-1" />
              <span className="text-xs text-gray-700">Chats</span>
            </button>
            <button 
              onClick={() => navigate('/wallet')}
              className="flex flex-col items-center p-2 hover:bg-gray-50 rounded-lg"
            >
              <Wallet className="w-5 h-5 text-green-500 mb-1" />
              <span className="text-xs text-gray-700">Wallet</span>
              {stats.balance > 0 && (
                <span className="text-xs text-green-600 font-semibold mt-1">
                  ₦{stats.balance.toLocaleString()}
                </span>
              )}
            </button>
            <button 
              onClick={() => setActiveTab('notifications')}
              className="flex flex-col items-center p-2 hover:bg-gray-50 rounded-lg relative"
            >
              <Bell className="w-5 h-5 text-purple-500 mb-1" />
              <span className="text-xs text-gray-700">Alerts</span>
              {stats.notifications > 0 && (
                <div className="absolute top-0 right-3 w-2 h-2 bg-red-500 rounded-full"></div>
              )}
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex overflow-x-auto space-x-2 mb-4 pb-2">
          {['overview', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-[#ff660e] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {activeTab === 'overview' && (
            <>
              {/* Account Status */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-bold text-black mb-3">Account Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Email Verification</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user?.is_email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user?.is_email_verified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Phone Verification</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user?.is_phone_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user?.is_phone_verified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Member Since</span>
                    <span className="text-sm">{formatDate(user?.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-bold text-black mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <button 
                    onClick={() => navigate('/favorites')}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Heart className="w-5 h-5 text-red-500" />
                      <span className="text-sm">My Favorites</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => navigate('/support')}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">Support Center</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  <button 
                    onClick={() => navigate('/security')}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-green-500" />
                      <span className="text-sm">Security Settings</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'settings' && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-bold text-black mb-4">Account Settings</h3>
              
              {/* Personal Information */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Full Name</label>
                    <div className="p-3 border rounded-lg bg-gray-50">
                      {user?.first_name} {user?.last_name}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Email</label>
                    <div className="p-3 border rounded-lg bg-gray-50">
                      {user?.email}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Phone</label>
                    <div className="p-3 border rounded-lg bg-gray-50">
                      {user?.phone_number || 'Not provided'}
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate('/settings')}
                    className="w-full border-2 border-[#ff660e] text-[#ff660e] py-2.5 rounded-lg font-semibold text-sm"
                  >
                    Edit Information
                  </button>
                </div>
              </div>

              {/* Change Password */}
              {/* <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Change Password</h4>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-sm text-[#ff660e]"
                  >
                    {showPassword ? 'Cancel' : 'Change'}
                  </button>
                </div>
                
                {showPassword && (
                  <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Current Password</label>
                      <div className="relative">
                        <input
                          type="password"
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          className="w-full p-2 border rounded"
                        />
                        <Key className="w-4 h-4 text-gray-400 absolute right-2 top-2.5" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">New Password</label>
                      <div className="relative">
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          className="w-full p-2 border rounded"
                        />
                        <Lock className="w-4 h-4 text-gray-400 absolute right-2 top-2.5" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Confirm Password</label>
                      <div className="relative">
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          className="w-full p-2 border rounded"
                        />
                        <Lock className="w-4 h-4 text-gray-400 absolute right-2 top-2.5" />
                      </div>
                    </div>
                    <button
                      onClick={handleChangePassword}
                      className="w-full bg-[#ff660e] text-white py-2 rounded-lg font-semibold"
                    >
                      Update Password
                    </button>
                  </div>
                )}
              </div> */}

              {/* Logout Button */}
              <button 
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 p-3 text-red-600 hover:bg-red-50 rounded-lg font-medium"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-black">Edit Profile</h3>
              <button onClick={() => setEditing(false)} className="p-1 hover:bg-gray-100 rounded">
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 block mb-1">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Location</label>
                <input
                  type="text"
                  name="state_name"
                  value={formData.state_name || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="flex-1 bg-[#ff660e] text-white py-3 rounded-lg font-semibold"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              {/* Profile Header */}
              <div className="text-center mb-6">
                <div className="relative inline-block mb-4">
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gradient-to-br from-[#ff660e] to-orange-500 mx-auto">
                    {user?.profile_picture_url ? (
                      <img 
                        src={getImageUrl(user.profile_picture_url)} 
                        alt={user.first_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 text-white" />
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => navigate('/settings')}
                    className="absolute bottom-2 right-2 bg-white p-2 rounded-full shadow-md border"
                  >
                    <Edit2 className="w-4 h-4 text-[#ff660e]" />
                  </button>
                </div>
                
                <h2 className="text-2xl font-bold text-black mb-1">
                  {user?.first_name} {user?.last_name}
                </h2>
                <p className="text-gray-600 mb-3">{user?.email}</p>
                
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="flex items-center">
                    <Star className="w-5 h-5 fill-current text-yellow-400 mr-1" />
                    <span className="font-semibold text-black">{stats.rating.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-1" />
                    {user?.state_name || 'Location'}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="mb-6">
                <button 
                  onClick={() => navigate('/wallet')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg mb-2"
                >
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-green-500" />
                    <div className="text-left">
                      <p className="text-sm font-medium">Wallet Balance</p>
                      <p className="text-lg font-bold text-black">
                        ₦{stats.balance.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
                
                <button 
                  onClick={() => setActiveTab('notifications')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg relative"
                >
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-purple-500" />
                    <div className="text-left">
                      <p className="text-sm font-medium">Notifications</p>
                      <p className="text-black">{stats.notifications} unread</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  {stats.notifications > 0 && (
                    <div className="absolute top-2 right-10 w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                </button>
              </div>

              {/* Quick Links */}
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/orders')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-5 h-5 text-blue-500" />
                    <span>My Orders</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={() => navigate('/favorites')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Heart className="w-5 h-5 text-red-500" />
                    <span>Favorites</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={() => navigate('/support')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-indigo-500" />
                    <span>Support</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 p-3 text-red-600 hover:bg-red-50 rounded-lg font-medium mt-4"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Account Overview */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-black mb-6">Account Overview</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div>
                  <h4 className="font-semibold text-black mb-4">Personal Information</h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <User className="w-5 h-5 text-[#ff660e]" />
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Mail className="w-5 h-5 text-[#ff660e]" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <Phone className="w-5 h-5 text-[#ff660e]" />
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium">{user?.phone_number || 'Not provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 border rounded-lg">
                      <MapPin className="w-5 h-5 text-[#ff660e]" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-medium">{user?.state_name || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div>
                  <h4 className="font-semibold text-black mb-4">Account Status</h4>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Email Verification</span>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          user?.is_email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user?.is_email_verified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      {!user?.is_email_verified && (
                        <button className="text-sm text-[#ff660e] hover:underline mt-2">
                          Resend verification email
                        </button>
                      )}
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Phone Verification</span>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          user?.is_phone_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user?.is_phone_verified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      {!user?.is_phone_verified && user?.phone_number && (
                        <button className="text-sm text-[#ff660e] hover:underline mt-2">
                          Verify phone number
                        </button>
                      )}
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Member Since</span>
                        <span className="font-medium">{formatDate(user?.created_at)}</span>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">User ID</span>
                        <span className="font-mono text-sm text-gray-500">{user?.user_id}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Edit Button */}
              <div className="mt-6 pt-6 border-t">
                <button
                  onClick={() => navigate('/settings')}
                  className="w-full md:w-auto border-2 border-[#ff660e] text-[#ff660e] px-6 py-3 rounded-lg font-semibold hover:bg-orange-50"
                >
                  Edit Profile Information
                </button>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-black mb-6">Security Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Change Password */}
                {/* <div>
                  <h4 className="font-semibold text-black mb-4">Change Password</h4>
                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Current Password"
                        className="w-full p-3 border rounded-lg"
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-gray-500"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="New Password"
                        className="w-full p-3 border rounded-lg"
                      />
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Confirm New Password"
                        className="w-full p-3 border rounded-lg"
                      />
                    </div>
                    <button
                      onClick={handleChangePassword}
                      className="w-full bg-[#ff660e] text-white py-3 rounded-lg font-semibold"
                    >
                      Update Password
                    </button>
                  </div>
                </div> */}

                {/* Security Information */}
                <div>
                  <h4 className="font-semibold text-black mb-4">Security Information</h4>
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <Shield className="w-5 h-5 text-blue-600" />
                        <span className="font-medium">Two-Factor Authentication</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Add an extra layer of security to your account</p>
                      <button className="text-sm text-blue-600 hover:underline">
                        Enable 2FA
                      </button>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-gray-600" />
                        <span className="font-medium">Last Login</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {new Date().toLocaleDateString('en-NG', { 
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Edit Profile Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-black">Edit Profile</h3>
              <button 
                onClick={() => setEditing(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 block mb-2">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-2">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-2">Location</label>
                <input
                  type="text"
                  name="state_name"
                  value={formData.state_name || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-lg"
                />
              </div>
              
              <div className="flex gap-4 pt-6">
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="flex-1 bg-[#ff660e] text-white py-3 rounded-lg font-semibold hover:bg-[#ff5500]"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

export default Profile;